import React, { useState, useEffect, useCallback } from "react";
import HistoryPopup from "./HistoryPopup";
import { useTranslation } from "../hooks/useTranslation";
import { useCss } from "../context/CssContext";
import config from "../config";

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http')) return url;
  const prefix = config.imgUrl && config.imgUrl.endsWith('/') ? config.imgUrl : `${config.imgUrl || ''}/`;
  const cleanedImg = url.startsWith('/') ? url.slice(1) : url;
  return `${prefix}${cleanedImg}`;
};

const DefaultAvatar = ({ customSrc }) => {
  const [src, setSrc] = React.useState(customSrc || '/static/image/default-avatar.png');
  React.useEffect(() => { setSrc(customSrc || '/static/image/default-avatar.png'); }, [customSrc]);
  return (
    <img
      src={src}
      alt="avatar"
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      onError={() => setSrc('/static/image/default-avatar.png')}
    />
  );
};

const Header = ({ username = "Kullanıcı" }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [staticLogo, setStaticLogo] = useState(null);
  const { t } = useTranslation();
  const { customLogoImage, customLogoEnabled, customAvatarImage, customAvatarEnabled } = useCss();

  const loadLogo = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(src);
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const tryLoad = async () => {
      for (const path of ['/static/image/logo.webp', '/static/image/logo.png']) {
        try {
          const loaded = await loadLogo(path);
          setStaticLogo(loaded);
          return;
        } catch { }
      }
      setStaticLogo(null);
    };
    tryLoad();
  }, [loadLogo]);

  const displayLogo = customLogoEnabled && customLogoImage ? getImageUrl(customLogoImage) : staticLogo;

  return (
    <>
      <div className="header-container">
        {/* Logo varsa üstte ortada; yoksa hiç render edilmez (üstte boşluk kalmaz) */}
        {displayLogo && (
          <div className="header-top-bar">
            <div className="header-logo-container">
              <img
                src={displayLogo}
                alt="Logo"
                className="header-logo"
                style={{
                  maxWidth: 'var(--logo-max-width, 260px)',
                  paddingTop: 'var(--logo-padding-top, 0px)',
                  paddingBottom: 'var(--logo-padding-bottom, 0px)',
                  marginTop: 'var(--logo-margin-top, 0px)',
                  marginBottom: 'var(--logo-margin-bottom, 0px)',
                }}
              />
            </div>
          </div>
        )}

        {/* Selamlama kartı ve Geçmiş butonu satırı */}
        <div className="header-actions-row">
          <div className="header-user-section">
            <div className="header-user-avatar">
              <DefaultAvatar customSrc={customAvatarEnabled && customAvatarImage ? getImageUrl(customAvatarImage) : null} />
            </div>
            <div className="header-user-info">
              <h1
                className="header-title"
                style={{ color: "var(--header-title-color)" }}
              >
                <span className="welcome-text">{t('header.welcome', 'Hoş geldiniz')}</span>{" "}
                <span
                  className="username-text"
                  style={{ color: "var(--header-username-color, #0ea5e9)" }}
                >
                  {username}
                </span>
              </h1>
              <p
                className="header-subtitle"
                style={{ color: "var(--header-subtitle-color)" }}
              >
                {t('header.subtitle', 'Talep etmek istediğiniz bonusu seçiniz.')}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="header-history-btn"
            onClick={() => setIsPopupOpen(true)}
            title={t('header.history', 'Geçmiş')}
            aria-label={t('header.history', 'Geçmiş')}
            style={{ color: "var(--header-icon-color)" }}
          >
            <svg
              className="header-history-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="header-history-text">{t('header.history', 'Geçmiş')}</span>
          </button>
        </div>
      </div>

      <HistoryPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Header;
